<script setup lang="ts">
import { Link } from "@inertiajs/vue3"
import { computed } from "vue"

import { ICON } from "@/lib/icons"
import { itemPath } from "@/routes"
import type { CaptureGroup } from "@/types/capture"

const props = withDefaults(
  defineProps<{
    group: CaptureGroup
    reviewing?: boolean
    reviewable?: boolean
  }>(),
  { reviewing: false, reviewable: true },
)

const emit = defineEmits<{
  review: [groupId: string]
}>()

const resolved = computed(
  () => props.group.status === "completed" || props.group.status === "skipped",
)

const resolutionLabel = computed(() => {
  if (props.group.resolution_kind === "created") return "Created"
  if (props.group.resolution_kind === "attached") return "Attached"
  if (props.group.status === "skipped") return "Skipped"
  return "Needs review"
})

const analysisLabel = computed(() => {
  if (props.group.analysis_status === "failed") return "Analysis failed"
  if (["queued", "running"].includes(props.group.analysis_status)) return "Analyzing"
  if (props.group.analysis_status === "succeeded") return "AI suggestion"
  return "Not analyzed"
})
</script>

<template>
  <article
    data-testid="group-card"
    :data-group-id="group.id"
    class="border-default bg-default overflow-hidden rounded-lg border"
  >
    <div
      class="bg-elevated grid min-h-36 gap-1 p-1"
      :class="group.photos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'"
    >
      <img
        v-for="photo in group.photos"
        :key="photo.id"
        :src="photo.large_url"
        :alt="`Capture ${photo.position} for group ${group.position}`"
        class="h-36 w-full rounded-sm object-cover"
      />
      <div
        v-if="group.photos.length === 0"
        class="text-muted flex min-h-36 items-center justify-center text-sm"
      >
        No photos
      </div>
    </div>

    <div class="space-y-3 p-4">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="text-muted text-xs">Group {{ group.position }} · {{ analysisLabel }}</p>
          <h2 class="text-highlighted truncate font-semibold">
            {{ group.analysis_data.name || "Unidentified item" }}
          </h2>
        </div>
        <UBadge :color="resolved ? 'neutral' : 'warning'" variant="subtle">
          {{ resolutionLabel }}
        </UBadge>
      </div>

      <p v-if="group.analysis_error" class="text-error text-sm" role="status">
        {{ group.analysis_error }}
      </p>

      <Link
        v-if="group.resolved_item"
        :href="itemPath({ id: group.resolved_item.id })"
        class="text-primary inline-flex min-h-11 items-center gap-2 text-sm font-medium"
      >
        {{ group.resolved_item.name }}
        <UIcon :name="ICON.arrowRight" class="size-4" />
      </Link>
      <button
        v-else-if="!resolved && reviewable"
        type="button"
        data-testid="review-group"
        class="bg-primary text-inverted min-h-11 w-full rounded-md px-4 text-sm font-medium"
        @click="emit('review', group.id)"
      >
        {{ reviewing ? "Close review" : "Review" }}
      </button>
    </div>

    <div v-if="reviewing && !resolved" class="border-default border-t p-4">
      <slot name="review" />
    </div>
  </article>
</template>
